import { forwardRef, type InputHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

/**
 * The fill is `bg-card`, not `bg-background` (flowstack#1403).
 *
 * `--background` is the PAGE's colour. Every `Input` in this app is rendered
 * inside a `<Card>`, and a card is by definition a different surface from the
 * page — so the page token painted a control the colour of the thing it stood
 * on. In light the two are 1.08:1 apart and the control vanished; in dark the
 * plane is darker than the panel, so the same mistake drew a plausible well
 * and hid itself. `bg-card` is the surface this control actually sits on, and
 * it separates in the one direction that is true in both themes: not at all,
 * which is why `--input` — not the fill — carries the 3:1 boundary.
 */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(
        "flex h-10 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    />
  )
);
Input.displayName = "Input";
